import React, { useState, useEffect, useRef } from "react";
import styled, { css } from "styled-components";
import PropTypes from "prop-types";
import { Formik, Field } from "formik";
import { TextField } from "formik-material-ui";
import cloneDeep from "lodash.clonedeep";
import { useHistory, useLocation } from "react-router-dom";

import { Page, Button, LoadMask, DialogModal } from "../../../../components";
import {
  Colors,
  MaterialQtdMode,
  USER_ROLES,
  MODULES,
  SUB_MODULES,
} from "../../../../constants";
import { materialValidationType, materialValidationMessage } from "./constants";
import AgentsModalTable from "./AgentsModalTable";
import MaterialModalTable from "./MaterialModalTable";
import FastFieldWrapper from "../../../../components/form/FastFieldWrapper";
import ManageMaterialModalTable from "./ManageMaterialModalTable";
import SelectedMaterialRow from "./SelectedMaterialRow";
import SelectedAgentRow from "./SelectedAgentRow";
import { RoutePaths } from "../../../../constants/RoutePaths";
import { mediaHelper } from "../../../../utils";
import { STATUS_AGENT } from "../../../../constants/agent/status";
import { MaterialFractionated } from "../../../../constants/MaterialFractionated";
import { InfoOutlined } from "@material-ui/icons";

const SectionWrapper = styled.div`
  display: flex;
  margin-bottom: 48px;
  flex-direction: column;

  ${mediaHelper.md`
    flex-direction: row;
  `}
`;

const SectionTitleContainer = styled.div`
  display: flex;
  align-items: flex-start;
  padding-bottom: 16px;

  ${mediaHelper.md`
    justify-content: flex-end;
    padding: 0px;
  `}
`;

const SectionTitleLabel = styled.div`
  width: 240px;
  font-size: 24px;
  color: ${Colors.SECONDARY_MEDIUM};

  ${mediaHelper.md`
    text-align: left;
  `}
`;

const CardSection = styled.div`
  flex: 1;
  display: flex;
  background-color: #fff;
  border-radius: 8px;
  border: 1px solid ${Colors.GRAY_LIGHT};
  box-sizing: border-box;
  flex-direction: column;

  ${mediaHelper.md`
    flex-direction: row;
    
    ${({ flexColumn }) =>
      flexColumn &&
      `
      flex-direction: column;
    `}
  `}
`;

const CardContent = styled.div`
  flex: 1;
  padding: 24px;

  ${({ block }) =>
    block
      ? `
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 24px;
  `
      : `
    display: flex;
    align-items: center;
    justify-content: space-between;
  `}

  ${({ blueBg }) =>
    blueBg &&
    `
    background-color: ${Colors.Alpha(Colors.PRIMARY_BLUE, 0.04)};
  `}
`;

const ContainerWithScroll = styled.div`
  max-height: ${({ maxHeight }) => maxHeight || "250px"};
  overflow-y: auto;
  padding-bottom: 10px;
`;

const HeaderCardContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const CardButtonTrash = styled(Button).attrs({
  color: "primary",
  variant: "text",
})`
  font-size: 12px;
  font-weight: 700;
`;

const LabelContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const CardTitleStyle = css`
  font-size: 16px;
  font-weight: 400;
  line-height: 28px;
  letter-spacing: 0.15px;
`;

const CardTitleBlue = styled.div`
  ${CardTitleStyle}

  color: ${Colors.PRIMARY_BLUE};
`;

const CardTitleBlack = styled.div`
  ${CardTitleStyle}

  color: #000;
`;

const CardSubTitle = styled.div`
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  letter-spacing: 0.15px;
  color: ${Colors.GRAY_DARKER};
`;

const AlertContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 16px;
  padding: 8px 16px;
  background-color: ${Colors.INFO_DARK};
  border-radius: 8px;
  color: ${Colors.WHITE};
`;

const AlertIcon = styled(InfoOutlined)`
  font-size: 24px;
  color: ${Colors.WHITE};
`;

const AlertInfoWrapper = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: center;
  flex-direction: column;
  gap: 4px;
`;

const AlertTitle = styled.span`
  font-size: 14px;
  font-weight: 700;
  color: ${Colors.WHITE};
  line-height: 20px;
`;

const AlertDescription = styled.span`
  font-size: 12px;
  font-weight: 400;
  color: ${Colors.WHITE};
  line-height: 16px;
`;

const SectionDivider = styled.div`
  width: 100%;
  height: 1px;
  flex: 1;
  background-color: ${Colors.GRAY_LIGHT};

  ${mediaHelper.md`
    height: 100%;
    width: 1px;

    ${({ horizontal }) =>
      horizontal &&
      `
      width: 100%;
      height: 1px;
      flex: 1;
    `}
  `}
`;

const PageActionButton = styled(Button).attrs(({ variant }) => ({
  size: "large",
  color: "secondary",
  variant: variant || "contained",
}))`
  ${mediaHelper.md`
    margin-left: 24px;
  `}
`;

const CardButton = styled(Button).attrs({
  size: "large",
  color: "primary",
  variant: "contained",
})`
  font-size: 14px;
  font-weight: 700;
`;

const Footer = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 16px;

  button + button {
    margin-left: 16px;
  }

  ${mediaHelper.md`
    margin-bottom: 0px;
    justify-content: flex-end;
  `}
`;

const StyledTextField = styled(Field)`
  width: 100%;
`;

const StyledSelectedMaterialRow = styled(SelectedMaterialRow)`
  box-shadow: 0px 3px 3px rgba(0, 0, 0, 0.2), 0px 3px 4px rgba(0, 0, 0, 0.14),
    0px 1px 8px rgba(0, 0, 0, 0.12);
  border-radius: 8px;
  background-color: #fff;
  margin-top: 16px;
`;

const Divider = styled.div`
  width: 100%;
  height: 8px;

  ${mediaHelper.md`
    height: 24px;
  `}
`;

const NewOrderLayout = ({
  userData,
  title,
  loading,
  agentList,
  materialList,
  agentSelectedList = [],
  onChangeAgentSelectedList,
  handleOnChangeNotIncludeAgents,
  tagBusinessUnityList,
  onRequestAgentList,
  onRequestMaterialList,
  handleNewOrderSubmit,
  handleValidations,
  handleConfirmDialog,
  loadingDialog,
  newOrderApproved,
  newOrderCreated,
  tmpMaterialSelected,
  tmpAgentSelected,
  tmpProductSelected,
  commomParams,
}) => {
  const history = useHistory();
  const location = useLocation();

  const [showAgentsModal, setShowAgentsModal] = useState(false);
  const [showMaterialModal, setShowMaterialModal] = useState(false);
  const [showManageMaterialModal, setShowManageMaterialModal] = useState(false);
  const [selectedMaterialIndex, setSelectedMaterialIndex] = useState(null);
  const [isOpenNewOrderDialog, setIsOpenNewOrderDialog] = useState(false);
  const [isOpenCancelOrderDialog, setIsOpenCancelOrderDialog] = useState(false);
  const [agentSkip, setAgentSkip] = useState(10000);
  const [materialSkip, setMaterialSkip] = useState(24);
  const [disableCreateOrder, setDisableCreateOrder] = useState(true);
  const nextLoadType = {
    agent: 0,
    material: 1,
  };
  const isAgentView = userData && userData.userRole === USER_ROLES.AGENT;

  useEffect(
    () => () => {
      tmpMaterialSelected.splice(0, tmpMaterialSelected.length);
      tmpAgentSelected.splice(0, tmpAgentSelected.length);
      tmpProductSelected.splice(0, tmpProductSelected.length);
    },
    []
  );

  useEffect(() => {
    if (showAgentsModal) onChangeAgentSelectedList([]);
  }, [showAgentsModal]);

  const _requestSearchFilter = (data, requestFn, increase, type) => {
    let filters = {};

    if (data.filters && data.filters.status) {
      filters.status = data.filters.status;
    }

    requestFn(
      {
        skip: data.skip || 0,
        take: data.take || 24,
        filters: {
          standard:
            data.term && data.type
              ? [
                  {
                    attribute: data.type,
                    value: data.term,
                  },
                ]
              : [],
          ...filters,
        },
        orderBy: data.orderBy || {},
      },
      increase
    );
  };

  const _getMaterialAmountQty = (material) => {
    return material?.agentList.reduce((sum, agent) => {
      return sum + agent.qty;
    }, 0);
  };

  const _handleMaterialRemove = ({
    materialByAgentList,
    index,
    setFieldValue,
  }) => {
    const copy = cloneDeep(materialByAgentList);
    copy.splice(index, 1);
    tmpMaterialSelected.splice(index, 1);

    setFieldValue("materialByAgentList", copy);
  };

  const _handleMaterialModeChange = ({
    checked,
    materialByAgentList,
    setFieldValue,
    index,
  }) => {
    const copy = cloneDeep(materialByAgentList);
    copy[index].mode = checked
      ? MaterialQtdMode.AUTOMATIC
      : MaterialQtdMode.MANUAL;

    if (checked) {
      copy[index].agentList = copy[index].agentList.map((agent) => ({
        ...agent,
        qty: copy[index].qty,
      }));
    }

    setFieldValue("materialByAgentList", copy);
  };

  const _handleMaterialQtyChange = ({
    qty,
    materialByAgentList,
    setFieldValue,
    index,
  }) => {
    const copy = cloneDeep(materialByAgentList);
    copy[index].mode = MaterialQtdMode.AUTOMATIC;

    copy[index].qty = qty;
    copy[index].agentList = copy[index].agentList.map((agent) => ({
      ...agent,
      qty,
    }));

    setFieldValue("materialByAgentList", copy);
  };

  const _handleMaterialIncrease = ({
    materialByAgentList,
    index,
    setFieldValue,
  }) => {
    const copy = cloneDeep(materialByAgentList);
    copy[index].mode = MaterialQtdMode.AUTOMATIC;

    const qty = copy[index].qty + 1;
    let finalQty;

    if (copy[index].labeled === MaterialFractionated.LABELED) {
      finalQty =
        Math.ceil(qty / copy[index].packingQuantity) *
        copy[index].packingQuantity;
    } else {
      finalQty = qty;
    }

    copy[index].qty = finalQty;

    copy[index].agentList = copy[index].agentList.map((agent) => ({
      ...agent,
      qty: finalQty,
    }));

    setFieldValue("materialByAgentList", copy);
  };

  const _handleMaterialDecrease = ({
    materialByAgentList,
    index,
    setFieldValue,
  }) => {
    const copy = cloneDeep(materialByAgentList);
    copy[index].mode = MaterialQtdMode.AUTOMATIC;

    const qty = copy[index].qty - 1;

    if (qty < 0) return;

    let finalQty;
    if (copy[index].labeled === MaterialFractionated.LABELED) {
      finalQty =
        Math.floor(qty / copy[index].packingQuantity) *
        copy[index].packingQuantity;
    } else {
      finalQty = qty;
    }

    copy[index].qty = finalQty;
    copy[index].agentList = copy[index].agentList.map((agent) => ({
      ...agent,
      qty: finalQty,
    }));

    setFieldValue("materialByAgentList", copy);
  };

  const _isThereAnyLabeledMaterial = ({ materialByAgentList }) => {
    return materialByAgentList.some(
      (material) => material.labeled === MaterialFractionated.LABELED
    );
  };

  const _handleMaterialDeleteAll = ({ setFieldValue, values }) => {
    setFieldValue("materialList", []);
    setFieldValue("materialByAgentList", []);
  };

  const _handleAgenteDeleteAll = ({ setFieldValue, values }) => {
    setFieldValue("agentList", []);
    onChangeAgentSelectedList([]);

    const newMaterialArray = values.materialByAgentList.map(
      (selectedMaterial, index) => ({
        ...selectedMaterial,
        agentList: selectedMaterial.agentList.splice(index, 1),
      })
    );

    newMaterialArray.forEach((material) => {
      _getMaterialAmountQty(material);
    });
  };

  const _handleAgentRemove = ({ agentList, index, setFieldValue, values }) => {
    const copy = cloneDeep(agentList);
    copy.splice(index, 1);
    tmpAgentSelected.splice(index, 1);

    setFieldValue("agentList", copy);

    const newMaterialArray = values.materialByAgentList.map(
      (selectedMaterial) => ({
        ...selectedMaterial,
        agentList: selectedMaterial.agentList.splice(index, 1),
      })
    );

    newMaterialArray.forEach((material) => {
      _getMaterialAmountQty(material);
    });
  };

  const _changeToUppercaseValue = ({ field, value, setFieldValue }) => {
    const uppercaseValue = value.toUpperCase();
    setFieldValue(field, uppercaseValue);
  };

  const _handleNewOrderIsValid = (values) => {
    const materialQtyByAgent = values.materialByAgentList.filter((item) =>
      item.agentList.some((obj) => obj.qty === 0 && obj.active)
    );
    const isMaterialQtyNotRounded = values.materialByAgentList
      .filter((item) => item.labeled === MaterialFractionated.LABELED)
      .some((item) => item.qty % item.packingQuantity !== 0);
   
    if (materialQtyByAgent.length > 0) {
      return true;
    }
    if (values.agentList.length <= 0) {
      return true;
    }
    if (values.materialList.length <= 0) {
      return true;
    }
    if (values.materialByAgentList.length <= 0) {
      return true;
    } 
    if (isMaterialQtyNotRounded) {
      return true;
    }
    return false;
  };

  const _onCloseAndGoToPage = () => {
    setIsOpenNewOrderDialog(false);
    history.push(RoutePaths.ORDER);
  };

  const _onConfirmCancelDialog = () => {
    setIsOpenCancelOrderDialog(false);
    history.push(RoutePaths.ORDER);
  };

  const _returnMaterialList = (data, values) => {
    const result = data.filter((item) =>
      values.materialByAgentList.every((obj) => obj.id !== item.id)
    );
    return result;
  };

  const _returnAgentList = (data, values) => {
    const result = data.filter((item) =>
      values.agentList.every((obj) => obj.id !== item.id)
    );
    return result;
  };

  const _handleMaterialData = (selectedData, setFieldValue, values) => {
    if (
      location.state &&
      location.state.material &&
      values.materialByAgentList.length > 0
    ) {
      tmpMaterialSelected.push(location.state.material);
      location.state = {};
    }

    selectedData.forEach((item) => {
      values.materialByAgentList.push({
        ...item,
        active: true,
        qty: 0,
        mode:
          values.agentList.length > item.availableQuantity
            ? MaterialQtdMode.MANUAL
            : MaterialQtdMode.AUTOMATIC,
        agentList: values.agentList.map((agent) => {
          const lastAgent = values.agentList.find(
            (agentItem) => agentItem.id === agent.id
          );
          return (
            lastAgent || {
              ...agent,
              active: true,
              qty: 0,
            }
          );
        }),
      });
      tmpMaterialSelected.push(item);
    });
    const hasMaterial = values.materialByAgentList.map((item) => item.id);
    if (
      location.state &&
      location.state.material &&
      values.materialByAgentList.length > 0
    ) {
      if (!hasMaterial.includes(values.materialByAgentList[0].id)) {
        values.materialByAgentList.unshift(location.state.material);
      }
    }
    setFieldValue("materialByAgentList", values.materialByAgentList);
    setFieldValue("materialList", tmpMaterialSelected);
  };

  const _handleAgentData = (selectedData, setFieldValue, values) => {
    const currentAgents = values.agentList;

    const newAgents = selectedData.filter(
      (item) => !currentAgents.some((value) => value.id === item.id)
    );

    const updatedList = [
      ...currentAgents,
      ...newAgents.map((agent) => ({
        ...agent,
        active: true,
        qty: 0,
      })),
    ];

    setFieldValue("agentList", updatedList);

    if (values.materialByAgentList.length) {
      setFieldValue(
        "materialByAgentList",
        values.materialByAgentList.map((selectedMaterial) => ({
          ...selectedMaterial,
          mode: selectedMaterial.mode,
          agentList: updatedList.map((agent) => {
            const lastAgent = selectedMaterial.agentList.find(
              (agentItem) => agentItem.id === agent.id
            );
            return (
              lastAgent || {
                ...agent,
                active: true,
                qty: selectedMaterial.qty,
              }
            );
          }),
        }))
      );
    }

    values.materialByAgentList.forEach((material, index) => {
      _getMaterialAmountQty({ ...material, agentList: updatedList });
    });
  };

  const _handleManageMaterial = (material, setFieldValue, values) => {
    setFieldValue(
      "materialByAgentList",
      values.materialByAgentList.map((obj) =>
        obj.id === material.id
          ? {
              ...obj,
              mode: material.mode,
              qty: _getMaterialAmountQty(material),
              agentList: material.agentList,
            }
          : obj
      )
    );
    _handleTotalMaterialByAgent(material);
  };

  const _handleTotalMaterialByAgent = (material) => {
    if (material.qty * material.agentList.length > material.availableQuantity) {
      setDisableCreateOrder(true);
      return;
    }
    setDisableCreateOrder(false);
  };

  const _getMaterialValidationType = ({
    qty,
    packingQuantity,
    availableQuantity,
    labeled,
  }) => {
    const isLabeled = labeled === MaterialFractionated.LABELED;
    const isPackingQuantityValid = qty % packingQuantity === 0;

    if (qty > availableQuantity) {
      return materialValidationType.EXCEEDS_AVAILABLE;
    }
    if (isLabeled && !isPackingQuantityValid) {
      return materialValidationType.INVALID_PACKING_QUANTITY;
    }
    if (isLabeled && isPackingQuantityValid) {
      return materialValidationType.VALID_PACKING_QUANTITY;
    }
    return materialValidationType.NONE;
  };

  const _isMaterialFieldError = ({
    qty,
    packingQuantity,
    availableQuantity,
    labeled,
  }) => {
    const validationType = _getMaterialValidationType({
      qty,
      packingQuantity,
      availableQuantity,
      labeled,
    });
    return (
      validationType === materialValidationType.EXCEEDS_AVAILABLE ||
      validationType === materialValidationType.INVALID_PACKING_QUANTITY
    );
  };

  const _messageHelperText = ({
    qty,
    packingQuantity,
    availableQuantity,
    labeled,
  }) => {
    const validationType = _getMaterialValidationType({
      qty,
      packingQuantity,
      availableQuantity,
      labeled,
    });

    if (validationType === materialValidationType.VALID_PACKING_QUANTITY) {
      const caixas = qty / packingQuantity;
      return materialValidationMessage[validationType](caixas);
    }
    if (validationType === materialValidationType.EXCEEDS_AVAILABLE) {
      return materialValidationMessage[validationType];
    }

    return materialValidationMessage[validationType] || "";
  };

  const _renderSelectedMaterial = ({
    material,
    index,
    values,
    setFieldValue,
  }) => {
    return (
      <StyledSelectedMaterialRow
        key={`materialByAgent-${index}`}
        title={material.name}
        canEdit={material.mode === MaterialQtdMode.AUTOMATIC}
        availableCount={parseInt(material.availableQuantity, 10)}
        qtdMode={material.mode}
        qty={material.qty}
        agentTotal={values.agentList.length}
        total={_getMaterialAmountQty(material)}
        isMaterialFieldError={_isMaterialFieldError({
          qty: material.qty,
          packingQuantity: material.packingQuantity,
          availableQuantity: material.availableQuantity,
          labeled: material.labeled,
        })}
        messageHelperText={_messageHelperText({
          qty: material.qty,
          packingQuantity: material.packingQuantity,
          availableQuantity: material.availableQuantity,
          labeled: material.labeled,
        })}
        materialLabeled={material.labeled === MaterialFractionated.LABELED}
        materialPackingQuantity={material.packingQuantity}
        handleIncrease={() => {
          _handleMaterialIncrease({
            materialByAgentList: values.materialByAgentList,
            index,
            setFieldValue,
          });
          _handleTotalMaterialByAgent(material);
        }}
        handleDecrease={() => {
          _handleMaterialDecrease({
            materialByAgentList: values.materialByAgentList,
            index,
            setFieldValue,
          });
          _handleTotalMaterialByAgent(material);
        }}
        handleQtdChange={(e) => {
          _handleMaterialQtyChange({
            qty: parseInt(e.target.value, 10),
            materialByAgentList: values.materialByAgentList,
            setFieldValue,
            index,
          });
          _handleTotalMaterialByAgent(material);
        }}
        handleModeChange={(value, checked) => {
          _handleMaterialModeChange({
            checked,
            materialByAgentList: values.materialByAgentList,
            setFieldValue,
            index,
          });
        }}
        handleRemove={() => {
          _handleMaterialRemove({
            materialByAgentList: values.materialByAgentList,
            index,
            setFieldValue,
          });
        }}
        handleEdit={() => {
          setSelectedMaterialIndex(index);
          setShowManageMaterialModal(true);
        }}
        isAgentView={isAgentView}
      />
    );
  };

  const _renderSelectedAgent = ({ agent, index, values, setFieldValue }) => (
    <SelectedAgentRow
      key={`agent-${index}`}
      title={agent.name}
      taxNumber={agent.cpf || agent.cnpj}
      customerCode={agent.customerCode}
      city={agent.city}
      uf={agent.uf}
      tags={agent.tags ? agent.tags : []}
      handleRemove={() => {
        _handleAgentRemove({
          agentList: values.agentList,
          index,
          setFieldValue,
          values,
        });
      }}
    />
  );

  return (
    <Formik
      initialValues={{
        agentList: isAgentView
          ? [
              {
                id: userData.id,
                name: userData.name,
                active: true,
                qty: 0,
              },
            ]
          : [],
        promotionalCicleName: "",
        materialList:
          location.state && location.state.material
            ? [location.state.material]
            : [],
        materialByAgentList:
          location.state && location.state.material
            ? [location.state.material]
            : [],
      }}
      validationSchema={handleValidations}
    >
      {({ values, setFieldValue }) => {
        return (
          <>
            <Page
              userData={userData}
              title={title}
              smallTitle
              originPath={RoutePaths.ORDER}
              originPathName="Pedidos"
            >
              <Divider />
              {!isAgentView && (
                <SectionWrapper>
                  <SectionTitleContainer>
                    <SectionTitleLabel>Representante</SectionTitleLabel>
                  </SectionTitleContainer>
                  <CardSection flexColumn>
                    <CardContent>
                      {values.agentList.length ? (
                        <LabelContent>
                          <CardTitleBlack>
                            {values.agentList.length} representantes
                            selecionados
                          </CardTitleBlack>
                          <CardSubTitle>
                            Revise os representantes na lista abaixo
                          </CardSubTitle>
                        </LabelContent>
                      ) : (
                        <LabelContent>
                          <CardTitleBlack>
                            Nenhum representante selecionado
                          </CardTitleBlack>
                          <CardSubTitle>
                            Selecione um representante
                          </CardSubTitle>
                        </LabelContent>
                      )}
                      <CardButton
                        onClick={() => {
                          setShowAgentsModal(true);
                        }}
                      >
                        Adicionar
                      </CardButton>
                    </CardContent>
                    {values.agentList.length ? (
                      <>
                        <div>
                          <SectionDivider horizontal />
                        </div>
                        <CardContent block blueBg>
                          <HeaderCardContent>
                            <CardTitleBlue>
                              Representantes selecionados
                            </CardTitleBlue>
                            {values.agentList.length > 1 ? (
                              <CardButtonTrash
                                onClick={() =>
                                  _handleAgenteDeleteAll({
                                    setFieldValue,
                                    values,
                                  })
                                }
                              >
                                Excluir tudo
                              </CardButtonTrash>
                            ) : null}
                          </HeaderCardContent>
                          <ContainerWithScroll>
                            {values.agentList.map((agent, index) =>
                              _renderSelectedAgent({
                                agent,
                                index,
                                values,
                                setFieldValue,
                              })
                            )}
                          </ContainerWithScroll>
                        </CardContent>
                      </>
                    ) : null}
                  </CardSection>
                </SectionWrapper>
              )}
              {userData.userRole !== USER_ROLES.AGENT && (
                <SectionWrapper>
                  <SectionTitleContainer>
                    <SectionTitleLabel>
                      Ciclo promocional desse pedido
                    </SectionTitleLabel>
                  </SectionTitleContainer>
                  <CardSection>
                    <CardContent>
                      <StyledTextField
                        name="promotionalCicleName"
                        type="text"
                        label="Ciclo promocional (opcional)"
                        variant="outlined"
                        component={TextField}
                        onChange={(e) => {
                          _changeToUppercaseValue({
                            field: "promotionalCicleName",
                            value: e.target.value,
                            setFieldValue,
                          });
                        }}
                      />
                    </CardContent>
                  </CardSection>
                </SectionWrapper>
              )}
              <SectionWrapper>
                <SectionTitleContainer>
                  <SectionTitleLabel>
                    Materiais a serem enviados
                  </SectionTitleLabel>
                </SectionTitleContainer>
                <CardSection flexColumn>
                  <CardContent>
                    {values.materialByAgentList.length ? (
                      <LabelContent>
                        <CardTitleBlack>
                          {values.materialByAgentList.length} materiais
                          selecionados
                        </CardTitleBlack>
                        <CardSubTitle>
                          Revise a quantidade de material a ser enviada para
                          cada representante na lista abaixo
                        </CardSubTitle>
                      </LabelContent>
                    ) : (
                      <LabelContent>
                        <CardTitleBlack>
                          Nenhum material selecionado
                        </CardTitleBlack>
                        <CardSubTitle>
                          Selecione pelo menos um material para enviar neste
                          pedido
                        </CardSubTitle>
                      </LabelContent>
                    )}
                    <CardButton
                      onClick={() => {
                        setShowMaterialModal(true);
                      }}
                    >
                      Adicionar material
                    </CardButton>
                  </CardContent>
                  <div>
                    <SectionDivider horizontal />
                  </div>
                  {values.materialByAgentList.length ? (
                    <CardContent block blueBg>
                      <HeaderCardContent>
                        <CardTitleBlue>Materiais selecionados</CardTitleBlue>
                        {values.materialList.length > 1 ? (
                          <CardButtonTrash
                            onClick={() =>
                              _handleMaterialDeleteAll({
                                setFieldValue,
                                values,
                              })
                            }
                          >
                            Excluir tudo
                          </CardButtonTrash>
                        ) : null}
                      </HeaderCardContent>
                      {_isThereAnyLabeledMaterial({
                        materialByAgentList: values.materialByAgentList,
                      }) ? (
                        <AlertContent>
                          <AlertIcon />
                          <AlertInfoWrapper>
                            <AlertTitle>Quantidade de produtos</AlertTitle>
                            <AlertDescription>
                              O preenchimento do campo quantidade de produtos
                              deve ser feito com uma quantidade que seja
                              múltipla da quantidade por caixa.
                            </AlertDescription>
                          </AlertInfoWrapper>
                        </AlertContent>
                      ) : null}
                      <ContainerWithScroll maxHeight="632px">
                        {values.materialByAgentList.map((material, index) =>
                          _renderSelectedMaterial({
                            material,
                            index,
                            values,
                            setFieldValue,
                          })
                        )}
                      </ContainerWithScroll>
                    </CardContent>
                  ) : null}
                </CardSection>
              </SectionWrapper>
              <Footer>
                <PageActionButton
                  variant="outlined"
                  type="button"
                  onClick={() => setIsOpenCancelOrderDialog(true)}
                >
                  cancelar
                </PageActionButton>
                <PageActionButton
                  type="submit"
                  onClick={() => {
                    setIsOpenNewOrderDialog(true);
                    const newValues = {
                      ...values,
                      materialByAgentList: values.materialByAgentList.map(
                        (element) => ({
                          ...element,
                          agentList: element.agentList.filter(
                            (agent) => agent.qty > 0
                          ),
                        })
                      ),
                    };
                    handleNewOrderSubmit(newValues);
                  }}
                  disabled={
                    _handleNewOrderIsValid(values) || disableCreateOrder
                  }
                >
                  Enviar pedido
                </PageActionButton>
              </Footer>
              {userData.userRole !== USER_ROLES.AGENT && (
                <FastFieldWrapper
                  name="agentList"
                  component={AgentsModalTable}
                  width={810}
                  open={showAgentsModal}
                  onDismiss={() => setShowAgentsModal(false)}
                  value={values.agentList}
                  agentSelectedList={agentSelectedList}
                  onChangeAgentSelectedList={onChangeAgentSelectedList}
                  handleOnChangeNotIncludeItems={handleOnChangeNotIncludeAgents}
                  onConfirm={(selectedAgentList) => {
                    setShowAgentsModal(false);
                    _handleAgentData(selectedAgentList, setFieldValue, values);
                  }}
                  tableData={_returnAgentList(agentList.list, values)}
                  loading={agentList.loading}
                  onSearch={(data) =>
                    _requestSearchFilter(
                      {
                        ...data,
                        take: 10000,
                        filters: { standard: [], status: STATUS_AGENT.ACTIVE },
                        orderBy: { attribute: "name", value: "ASC" },
                      },
                      onRequestAgentList
                    )
                  }
                  take={10000}
                  onLoad={(data) => {
                    setAgentSkip(agentSkip + 10000);
                    _requestSearchFilter(
                      {
                        ...data,
                        take: 10000,
                        filters: { standard: [], status: STATUS_AGENT.ACTIVE },
                        orderBy: { attribute: "name", value: "ASC" },
                      },
                      onRequestAgentList,
                      true,
                      nextLoadType.agent
                    );
                  }}
                  handleRefresh={() => {
                    onRequestAgentList({
                      ...commomParams,
                      take: 10000,
                      filters: { standard: [], status: STATUS_AGENT.ACTIVE },
                      orderBy: { attribute: "name", value: "ASC" },
                    });
                  }}
                  tagBusinessUnityList={tagBusinessUnityList}
                />
              )}
              <FastFieldWrapper
                name="materialList"
                component={MaterialModalTable}
                width={810}
                open={showMaterialModal}
                onDismiss={() => setShowMaterialModal(false)}
                agentSelectedList={agentSelectedList}
                onChangeAgentSelectedList={onChangeAgentSelectedList}
                onConfirm={(selectedMaterialList) => {
                  setShowMaterialModal(false);
                  _handleMaterialData(
                    selectedMaterialList,
                    setFieldValue,
                    values
                  );
                }}
                tableData={_returnMaterialList(materialList.list, values)}
                loading={materialList.loading}
                onSearch={(data) =>
                  _requestSearchFilter(
                    {
                      ...data,
                      orderBy: { attribute: "quantity", value: "DESC" },
                    },
                    onRequestMaterialList,
                    false,
                    nextLoadType.material
                  )
                }
                take={24}
                onLoad={(data) => {
                  _requestSearchFilter(
                    {
                      ...data,
                      orderBy: { attribute: "quantity", value: "DESC" },
                    },
                    onRequestMaterialList,
                    true,
                    nextLoadType.material
                  );
                }}
                handleRefresh={() => {
                  onRequestMaterialList({
                    ...commomParams,
                    orderBy: { attribute: "quantity", value: "DESC" },
                  });
                }}
                notSelectAll
              />
              <FastFieldWrapper
                name="materialByAgentList"
                component={ManageMaterialModalTable}
                width={810}
                open={showManageMaterialModal}
                onDismiss={() => {
                  setShowManageMaterialModal(false);
                  setSelectedMaterialIndex(null);
                }}
                onConfirm={(data) => {
                  setShowManageMaterialModal(false);
                  setSelectedMaterialIndex(null);
                  _handleManageMaterial(data, setFieldValue, values);
                }}
                onChangeAgentSelectedList={onChangeAgentSelectedList}
                materialIndex={selectedMaterialIndex}
                getMaterialAmountQty={_getMaterialAmountQty}
                showManageMaterialModal={showManageMaterialModal}
              />
              {loading ? <LoadMask /> : null}
              <DialogModal
                title={
                  newOrderCreated
                    ? "Pedido enviado com sucesso!"
                    : "Novo pedido"
                }
                description={newOrderCreated ? "" : "Deseja criar o pedido?"}
                confirmButtonText={
                  // userData.userRole === USER_ROLES.EMPLOYEE ||
                  newOrderCreated ? "" : "Enviar"
                }
                cancelButtonText={newOrderCreated ? "Fechar" : "Cancelar"}
                hideCancelButton={newOrderCreated ? true : false}
                hideConfirmButton={
                  newOrderCreated
                    ? // userData.userRole === USER_ROLES.EMPLOYEE ||
                      // (userData.userPermissions &&
                      //   !userData.userPermissions[
                      //     SUB_MODULES[MODULES.DISTRIBUTIONS].APPROVAL_ORDERS
                      //   ][4])
                      true
                    : false
                }
                enableConfirm={!loadingDialog}
                closeButtonText={newOrderCreated ? "Fechar" : "Cancelar"}
                size="lg"
                showCloseButton={newOrderCreated ? true : false}
                handleConfirmDialog={() => {
                  handleConfirmDialog();
                }}
                handleCloseDialog={() => {
                  if (loadingDialog) return;
                  newOrderCreated
                    ? _onCloseAndGoToPage()
                    : setIsOpenNewOrderDialog(false);
                }}
                handleOnlyClose={() => {
                  if (loadingDialog) return;
                  newOrderCreated
                    ? _onCloseAndGoToPage()
                    : setIsOpenNewOrderDialog(false);
                }}
                isOpenDialog={isOpenNewOrderDialog}
                loadingDialog={loadingDialog}
              />
              <DialogModal
                title="Descartar pedido"
                description="Tem certeza que deseja sair sem salvar as alterações?"
                confirmButtonText="Descartar pedido"
                handleConfirmDialog={() => {
                  _onConfirmCancelDialog();
                }}
                handleCloseDialog={() => {
                  setIsOpenCancelOrderDialog(false);
                }}
                isOpenDialog={isOpenCancelOrderDialog}
              />
            </Page>
          </>
        );
      }}
    </Formik>
  );
};

NewOrderLayout.propTypes = {
  userData: PropTypes.object,
  loading: PropTypes.bool,
  title: PropTypes.string,
  agentList: PropTypes.shape({
    list: PropTypes.any,
    loading: PropTypes.any,
    total: PropTypes.any,
  }).isRequired,
  materialList: PropTypes.shape({
    list: PropTypes.any,
    loading: PropTypes.any,
    total: PropTypes.any,
  }).isRequired,
  loadingDialog: PropTypes.bool,
  newOrderApproved: PropTypes.bool,
  newOrderCreated: PropTypes.bool,
  tmpMaterialSelected: PropTypes.array,
  tmpAgentSelected: PropTypes.array,
  tmpProductSelected: PropTypes.array,
  commomParams: PropTypes.object,
};

NewOrderLayout.defaultProps = {
  userData: {},
  loading: false,
  title: "",
  loadingDialog: false,
  newOrderApproved: false,
  newOrderCreated: false,
  tmpMaterialSelected: [],
  tmpAgentSelected: [],
  tmpProductSelected: [],
  commomParams: {},
};

export default NewOrderLayout;
